/* eslint-disable camelcase */
import { observer } from "mobx-react-lite"
import React, { FC, useCallback, useEffect, useMemo } from "react"
import { ActivityIndicator, Appearance, FlatList, ViewStyle } from "react-native"
import { Button, JalaIcon, LazyRender, Screen, Text } from "../../../../components"
// import { useStores } from "../../models"
import { CompositeScreenProps } from "@react-navigation/native"
import _ from "lodash"
import moment from "moment"
import numeral from "numeral"
import { translate, TxKeyPath } from "../../../../i18n"
import { useStores } from "../../../../models"
import { goBack, navigate } from "../../../../navigators"
import { color } from "../../../../theme"
import { delay } from "../../../../utils/delay"
import { View } from "native-base"

const ROOT: ViewStyle = {
  flex: 1,
}

const FLATLIST_WRAPPER: ViewStyle = {
  paddingTop: 8
}

const CYCLE_WRAPPER: ViewStyle = {
  height: 172,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: color.palette.gray200,
  marginHorizontal: 16,
  marginVertical: 8,
  justifyContent: 'flex-start',
}

const CYCLE_HEADER: ViewStyle = {
  width: '100%',
  paddingHorizontal: 12,
  paddingVertical: 8,
  borderBottomWidth: 0.5,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  alignItems: 'center'
}

const ICON_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  alignItems: 'center',
  marginLeft: 10
}

const ICON_WRAPPER: ViewStyle = {
  backgroundColor: color.palette.orange500,
  width: 16,
  height: 16,
  alignItems: 'center',
  borderRadius: 50,
  padding: 1,
  marginRight: 3
}

const CYCLE_CONTENT_WRAPPER: ViewStyle = {
  flex: 1,
  flexDirection: 'row',
  width: '100%',
  paddingTop: 8
}

const CYCLE_CONTENT: ViewStyle = {
  flex: 1,
  paddingHorizontal: 12,
}

const CYCLE_CONTENT_ITEM: ViewStyle = {
  flexDirection: 'row',
  marginBottom: 10
}

export const CycleHistoryTitle: FC<{ colorScheme?: "light" | "dark", tintColor?: string }> = observer(function CycleHistoryTitle(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), tintColor } = props
  const { appStore: { farmStore: { farm, pondStore: { pond } } } } = useStores();
  return <View>
    <Text tx="cycleHistoryScreen.header" preset="title2" colorScheme={colorScheme} color={tintColor} />
    <Text text={`${farm.name} - ${pond.name}`} preset="body3" colorScheme={colorScheme} color={tintColor} />
  </View>
})

const CycleHistoryHeader = ({ label, pro, loading = false, colorScheme }: { label?: string, pro?: any, loading?: boolean, colorScheme?: "light" | "dark" }) => (
  <View style={CYCLE_HEADER} justifyContent="space-between">
    <View flexDirection="row">
      <Text preset="title3" text={label} colorScheme={colorScheme} />
      {pro && (
        <View style={ICON_CONTAINER}>
          <View style={ICON_WRAPPER}>
            <JalaIcon name="premium" size={12} color={color.palette.white} />
          </View>
          <Text preset="title4" text="Pro" colorScheme={colorScheme} />
        </View>
      )}
    </View>
    {loading && <ActivityIndicator />}
  </View>
)

const CycleContentItem = ({ title, value, unit, colorScheme }: { title?: string, value?: any, unit?: string, colorScheme?: "light" | "dark" }) => (
  <View>
    <Text preset="body2" tx={`cycleHistoryScreen.${_.camelCase(title)}` as TxKeyPath} colorScheme={colorScheme} />
    <View style={CYCLE_CONTENT_ITEM}>
      <Text preset="title2" text={value || "-"} colorScheme={colorScheme} />
      {unit && <Text preset="title2" text={unit} tx={`cycleHistoryScreen.${_.camelCase(unit)}` as TxKeyPath} marginLeft="1.5" colorScheme={colorScheme} />}
    </View>
  </View>
)

const CycleHistory = ({ data, onPress }: { data?: any, onPress?: (data) => void }) => {
  const { started_at, finished_at, pro, age, total_seed, cycle_actuals = [], cycle_predictions = [], loading } = data;
  const startedAt = moment(started_at).format('DD MMM YYYY');
  const finishedAt = finished_at ? moment(finished_at).format('DD MMM YYYY') : translate('now');
  const cycleSummary = cycle_actuals[0] || cycle_predictions[0] || {};
  const colorScheme = "light" || Appearance.getColorScheme();
  return (
    <Button
      testID="jala:id/cycle-histories"
      preset="link"
      style={CYCLE_WRAPPER}
      disabled={loading}
      onPress={onPress}>
      <CycleHistoryHeader label={`${startedAt} - ${finishedAt}`} pro={pro} loading={loading} colorScheme={colorScheme} />
      <View style={CYCLE_CONTENT_WRAPPER}>
        <View style={CYCLE_CONTENT}>
          <CycleContentItem title="age" value={age} unit="days" colorScheme={colorScheme} />
          <CycleContentItem title="harvest" value={cycleSummary.harvest_accumulation_weight && numeral(cycleSummary.harvest_accumulation_weight).format('0,0.[00]') || '-'} unit="kg" colorScheme={colorScheme} />
        </View>
        <View style={CYCLE_CONTENT}>
          <CycleContentItem title="seed" value={total_seed && numeral(total_seed).format('0,0.[00]') || '-'} colorScheme={colorScheme} />
          <CycleContentItem title="fcr" value={cycleSummary.fcr && numeral(cycleSummary.fcr).format('0,0.[00]') || '-'} colorScheme={colorScheme} />
        </View>
        <View style={CYCLE_CONTENT}>
          <CycleContentItem title="size" value={cycleSummary.size && numeral(cycleSummary.size).format('0,0.[00]') || '-'} colorScheme={colorScheme} />
          <CycleContentItem title="survival" value={cycleSummary.survival_rate && numeral(cycleSummary.survival_rate).format('0,0.[00]') || '-'} unit="%" colorScheme={colorScheme} />
        </View>
      </View>
    </Button>
  )
}

export const CycleHistoryScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function CycleHistoryScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props

  // Pull in one of our MST stores
  const {
    appStore: { farmStore, farmStore: { pondStore } },
    farmPondStore
  } = useStores();

  useEffect(() => {
    if (!pondStore) return goBack('Home.Stack')
    pondStore.getCycleActualAndPredictions()
  }, [pondStore])

  const cycles = useMemo(() => (
    pondStore.cycleStoreModels.map((cycleStore) => ({
      ...cycleStore.cycle,
      cycle_predictions: [...cycleStore.cyclePredictions],
      cycle_actuals: [...cycleStore.cycleActuals],
      loading: pondStore.loadingCycleActualAndPrediction
    }))
  ), [pondStore.cycleStoreModels, pondStore.loadingCycleActualAndPrediction])

  const onNavigate = async (data) => {
    const { pond_id, id } = data;

    if (farmPondStore.navigating) return;
    farmPondStore.set('navigating', true)
    await delay(0);
    farmStore.set('pondId', pond_id)
    farmStore.pondStore.set('cycleId', id)
    navigate('Home.Stack', "Home.Cycle.Stack", { data })
    farmPondStore.set('navigating', false)
  }

  const keyExtractor = useCallback((item) => `${item.id}`, [])

  return (
    <Screen
      style={ROOT} preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={color.primary}
      statusBar="light-content"
    >
      <LazyRender>
        {/* {!!cycles && <FlatList data={cycles} renderItem={CycleHistory} contentContainerStyle={FLATLIST_WRAPPER} /> } */}
        {!!cycles && <FlatList
          data={cycles}
          renderItem={({ item }) => <CycleHistory data={item} onPress={() => onNavigate(item)} />}
          contentContainerStyle={FLATLIST_WRAPPER}
          keyExtractor={keyExtractor}
        /> }
        {/* <FlatList data={cycles} renderItem={CycleHistory} contentContainerStyle={FLATLIST_WRAPPER} /> */}
      </LazyRender>
    </Screen>
  )
})
